import axios from "axios";

const API_URL =
  process.env.REACT_APP_API_URL || "https://dailybackend-zvvt.onrender.com";

// Create axios instance with default config
const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect on 401 errors for authenticated routes (not login/register)
    const isAuthRoute = error.config?.url?.includes("/auth/");
    if (error.response?.status === 401 && !isAuthRoute) {
      console.log("Token expired or invalid, redirecting to login...");
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export const authService = {
  async login(username, password) {
    const response = await api.post("/auth/login", { username, password });
    return response.data;
  },

  async register(userData) {
    const response = await api.post("/auth/register", userData);
    return response.data;
  },

  async registerRegularUser(userData) {
    const response = await api.post("/regular-users/register", userData);
    return response.data;
  },

  async registerBusiness(userData) {
    const response = await api.post("/businesses/register", userData);
    return response.data;
  },

  async getCurrentUser() {
    const response = await api.get("/auth/me");
    return response.data;
  },

  async updateUserProfile(userData) {
    const response = await api.put("/regular-users/profile", userData);
    return response.data;
  },

  async updateBusinessProfile(businessData) {
    const response = await api.put("/businesses/profile", businessData);
    return response.data;
  },

  logout() {
    localStorage.removeItem("token");
    window.location.href = "/login";
  },
};

export const userService = {
  async getAllUsers() {
    const response = await api.get("/users");
    return response.data;
  },

  async createUser(userData) {
    const response = await api.post("/users", userData);
    return response.data;
  },

  async updateUser(id, userData) {
    const response = await api.put(`/users/${id}`, userData);
    return response.data;
  },

  async deleteUser(id) {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },

  async getUserById(id) {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  async getUserDeletionStats(id) {
    const response = await api.get(`/users/${id}/deletion-stats`);
    return response.data;
  },
};

export const eventService = {
  async getAllEvents(start, end) {
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;

    const response = await api.get("/events", { params });
    return response.data;
  },

  async getAllEventsAdmin(start, end) {
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;

    const response = await api.get("/events/all", { params });
    return response.data;
  },

  async createEvent(eventData) {
    const response = await api.post("/events", eventData);
    return response.data;
  },

  async updateEvent(id, eventData) {
    const response = await api.put(`/events/${id}`, eventData);
    return response.data;
  },

  async deleteEvent(id) {
    const response = await api.delete(`/events/${id}`);
    return response.data;
  },

  async getEventById(id) {
    const response = await api.get(`/events/${id}`);
    return response.data;
  },
};

export const dailyItemService = {
  // Admin functions
  async getAllDailyItemsAdmin(
    startDate,
    endDate,
    userId,
    includeStatus = null
  ) {
    const params = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (userId) params.userId = userId;
    if (includeStatus) params.includeStatus = includeStatus;

    const response = await api.get("/daily-items/admin/all", { params });
    return response.data;
  },

  async createDailyItem(itemData) {
    const response = await api.post("/daily-items", itemData);
    return response.data;
  },

  async updateDailyItem(id, itemData) {
    const response = await api.put(`/daily-items/${id}`, itemData);
    return response.data;
  },

  async deleteDailyItem(id) {
    const response = await api.delete(`/daily-items/${id}`);
    return response.data;
  },

  // User functions
  async getUserDailyItems(startDate, endDate) {
    const params = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await api.get("/daily-items", { params });
    return response.data;
  },

  // Delivery logs
  async getDeliveryLogs(startDate, endDate, userId) {
    const params = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (userId) params.userId = userId;

    const response = await api.get("/daily-items/delivery-logs", { params });
    return response.data;
  },

  async updateDeliveryStatus(logId, status, notes) {
    const response = await api.patch(
      `/daily-items/delivery-logs/${logId}/status`,
      {
        status,
        notes,
      }
    );
    return response.data;
  },

  async raiseConcern(logId, message) {
    const response = await api.post(
      `/daily-items/delivery-logs/${logId}/concerns`,
      {
        message,
      }
    );
    return response.data;
  },

  async respondToConcern(logId, concernId, adminResponse, status) {
    const response = await api.patch(
      `/daily-items/delivery-logs/${logId}/concerns/${concernId}`,
      {
        adminResponse,
        status,
      }
    );
    return response.data;
  },

  async deleteConcern(logId, concernId) {
    const response = await api.delete(
      `/daily-items/delivery-logs/${logId}/concerns/${concernId}`
    );
    return response.data;
  },

  async updateDeliveryQuantity(logId, quantity, reason) {
    const response = await api.put(
      `/daily-items/delivery-logs/${logId}/quantity`,
      {
        quantity: parseInt(quantity),
        reason,
      }
    );
    return response.data;
  },

  async regenerateIndefiniteLogs() {
    const response = await api.post(`/daily-items/regenerate-indefinite-logs`);
    return response.data;
  },
};

export const businessUserService = {
  async getAllUsers() {
    const response = await api.get("/business-user-relations/users");
    // Transform the data to match the expected format for dropdowns
    if (response.data.relations) {
      return response.data.relations.map((relation) => ({
        _id: relation.userId._id,
        username: relation.userId.username,
        firstName: relation.userId.firstName,
        lastName: relation.userId.lastName,
        email: relation.userId.email,
        uniqueUserId: relation.userUniqueId,
        fullName: `${relation.userId.firstName} ${relation.userId.lastName}`,
        role: "user",
        accountType: "regular_user",
      }));
    }
    return [];
  },

  async getBusinessUsers() {
    const response = await api.get("/business-user-relations/users");
    return response.data;
  },

  async getBusinessUsersForAssignment() {
    const response = await api.get("/business-user-relations/users");
    // Transform the data to match the expected format for daily items
    if (response.data.relations) {
      return response.data.relations.map((relation) => ({
        _id: relation.userId._id,
        username: relation.userId.username,
        firstName: relation.userId.firstName,
        lastName: relation.userId.lastName,
        email: relation.userId.email,
        uniqueUserId: relation.userUniqueId,
        fullName: `${relation.userId.firstName} ${relation.userId.lastName}`,
        role: "user",
        accountType: "regular_user",
        status: relation.status, // Include the status from the business-user relation
        relationshipType: relation.relationshipType, // Include relationship type as well
      }));
    }
    return [];
  },

  async addUserById(userUniqueId, relationshipData) {
    const response = await api.post("/business-user-relations/add-user", {
      userUniqueId,
      ...relationshipData,
    });
    return response.data;
  },

  async removeUser(relationId) {
    const response = await api.delete(
      `/business-user-relations/users/${relationId}`
    );
    return response.data;
  },

  async updateUserRelation(relationId, updateData) {
    const response = await api.put(
      `/business-user-relations/users/${relationId}`,
      updateData
    );
    return response.data;
  },

  async searchUser(userUniqueId) {
    const response = await api.get(
      `/business-user-relations/search-user/${userUniqueId}`
    );
    return response.data;
  },

  async getStats() {
    const response = await api.get("/business-user-relations/stats");
    return response.data;
  },
};

export default api;
